import { useCallback, useMemo, useState, type CSSProperties, type SyntheticEvent } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next/pages';
import cn from 'clsx';
import { getVirtualTourType } from '@pkgs/components/utils/propertyCardUtils';
import { getUploadcareImageTransformationProps } from '@pkgs/components/utils/getUploadcareImageTransformationProps';
import { getObjectPosition } from '@pkgs/components/utils/getImageObjectPosition';
import { getBadges } from '@pkgs/components/utils/badgesUtils';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { Badge, BadgeSize } from '@pkgs/components/components/Badge/Badge';
import { useCarouselActions, AiBadge, getImageAltWithAiIndicator, type TFunction } from '@pkgs/components';
import { getUploadCareImageUrl } from '@pkgs/api';
import { Wrapper } from '@ev-tech/ui-core-components/Wrapper';

import { getAiBadgeTranslationKey } from '@/utils/aiBadgeUtils';
import { PageVariant } from '@/types/expose';
import { useExposeModal } from '@/providers/ExposeModalProvider';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useShouldHideUserAccountExclusiveContent } from '@/hooks/useShouldHideUserAccountExclusiveContent';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';
import { ImageWithFallBack } from '@/components/common/ImageComponent/ImageWithFallBack';

import { ExposeHeroImagePrivateOffice } from './PrivateOfficeExposeHeroImage';
import { NoImageFallback } from './NoImageFallback';
import { MobileHeroImageSlider } from './MobileHeroImageSlider';
import { TabKeys } from '../MediaTabs/MediaTabs.types';
import { ImageButton } from '../ImageButton/ImageButton';

import styles from './ExposeHeroImage.module.css';

const ActionButtons = dynamic(() => import('./ActionButtons').then((mod) => ({ default: mod.ActionButtons })));

type HeroImagePortraitSlot = 'mainImage' | 'secondImage' | 'thirdImage';

const portraitBySlotInitial: Record<HeroImagePortraitSlot, boolean> = {
  mainImage: true,
  secondImage: true,
  thirdImage: true,
};

/** Portrait tiles use contain + center; landscape uses cover + focal point when available */
const getHeroImageObjectPosition = (isPortrait: boolean, image: Parameters<typeof getObjectPosition>[0]): string =>
  isPortrait ? 'center' : getObjectPosition(image);

export const ExposeHeroImage = () => {
  const { pageVariant } = useExposePageProps();
  const {
    data: { listing, genericListing },
  } = useListing();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_card);
  const { t: tPropertyExpose } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const isUAExclusiveListingAndNotSignedIn = useShouldHideUserAccountExclusiveContent();
  const carouselActions = useCarouselActions();
  const [portraitByHeroSlot, setPortraitByHeroSlot] = useState(portraitBySlotInitial);
  const slicedImages = useMemo(() => genericListing.propertyImages?.slice(0, 3), [genericListing.propertyImages]) ?? [];
  const { openMediaModal, setSelectedMediaTab, setSelectedMediaImageIndex } = useExposeModal();
  const { isDesktop } = useBreakpoint();

  const isPrivateOffice = pageVariant !== PageVariant.EXPOSE && pageVariant !== PageVariant.DEVELOPMENT_PROJECT;

  const onHeroImageLoad = useCallback((event: SyntheticEvent<HTMLImageElement>, slot: HeroImagePortraitSlot) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setPortraitByHeroSlot((prev) => ({ ...prev, [slot]: naturalWidth < naturalHeight }));
  }, []);

  const handleImageClick = useCallback(
    (index: number) => {
      setSelectedMediaTab(TabKeys[isDesktop ? 'Photos' : 'Gallery']);
      setSelectedMediaImageIndex(index);

      openMediaModal();
    },
    [isDesktop, openMediaModal, setSelectedMediaImageIndex, setSelectedMediaTab],
  );

  const showsButtonGroup = useMemo(
    () =>
      Boolean(genericListing.propertyImages?.length) ||
      Boolean(listing?.floorPlanImages?.length) ||
      Boolean(listing?.videoUrlNormalized) ||
      Boolean(listing?.vtourUrlNormalized),
    [genericListing.propertyImages?.length, listing?.floorPlanImages?.length, listing?.videoUrlNormalized, listing?.vtourUrlNormalized],
  );

  const badges = useMemo(
    () =>
      getBadges({
        imageDisturbers: genericListing.imageDisturbers,
        translationResolver: t as TFunction,
        countryAlpha2: genericListing.countryAlpha2,
        propertyMarketingType: genericListing.propertyMarketingType,
      }),
    [genericListing.imageDisturbers, t, genericListing.propertyMarketingType, genericListing.countryAlpha2],
  );

  if (!genericListing.propertyImages?.length && !isUAExclusiveListingAndNotSignedIn) {
    return <NoImageFallback showsButtonGroup={showsButtonGroup} />;
  }

  if (isPrivateOffice || isUAExclusiveListingAndNotSignedIn) {
    return (
      <ExposeHeroImagePrivateOffice
        carouselActions={carouselActions}
        showsButtonGroup={showsButtonGroup}
        onImageClick={() => handleImageClick(carouselActions.selectedIndex)}
        badges={badges}
        isUAExclusiveListing={isUAExclusiveListingAndNotSignedIn}
      />
    );
  }

  const mainImage = slicedImages.find((image) => image.main) ?? slicedImages[0];
  const secondImage = slicedImages[1];
  if (!mainImage) {
    return <NoImageFallback showsButtonGroup={showsButtonGroup} />;
  }

  const vTourType = getVirtualTourType(listing?.vtourUrlNormalized, listing?.videoUrlNormalized);

  return (
    <Wrapper className={styles.wrapper} width={{ tabletLandscape: 'l', laptop: 'l' }}>
      {/* data-testid wrapper since the ui-core Wrapper component does not forward arbitrary props to the DOM */}
      <div className={styles.heroImageRoot} data-testid="expose-hero-image">
        <div className={styles.badgeContainer}>
          {badges.map(
            (badge) =>
              badge.label && (
                <Badge
                  key={badge.label}
                  label={badge.label}
                  variant={badge.variant}
                  size={BadgeSize.M}
                  data-testid={`expose-hero-image_badge_${badge.label}`}
                />
              ),
          )}
        </div>
        <div className={styles.imageGalleryRow} data-image-count={slicedImages.length - 1}>
          <ImageButton
            className={cn(styles.imageWrapper, styles.imageWrapperBig0)}
            onClick={() => {
              handleImageClick(0);
            }}
            data-testid="property-hero-small-image-0"
            data-one-image={slicedImages.length === 1}
          >
            <AiBadge
              aiType={mainImage.aiType}
              t={tPropertyExpose as TFunction}
              translationKey={getAiBadgeTranslationKey({ size: 'm' })}
              offset="m"
              size="m"
              side="left"
            />
            <ImageWithFallBack
              className={styles.image}
              src={getUploadCareImageUrl({ imageId: mainImage.id, useWaterMark: listing?.hasWaterMark })}
              alt={getImageAltWithAiIndicator({
                baseAlt: mainImage.altText || mainImage.title,
                imageAiType: mainImage.aiType,
                t: tPropertyExpose as TFunction,
              })}
              onLoad={(event) => onHeroImageLoad(event, 'mainImage')}
              style={
                {
                  '--object-fit': portraitByHeroSlot.mainImage ? 'contain' : 'cover',
                  '--object-position': getHeroImageObjectPosition(portraitByHeroSlot.mainImage, mainImage),
                } as CSSProperties
              }
              sizes={slicedImages.length === 1 ? '(min-width: 904px) 1128px, 75vw' : '(min-width: 904px) 752px, 75vw'}
              loading="eager"
              fetchPriority="high"
              fill
              {...getUploadcareImageTransformationProps({
                aspectRatio: slicedImages.length === 1 ? '21/9' : '3/2',
                fit: 'automatic',
                image: mainImage,
              })}
            />
          </ImageButton>
          {slicedImages.length === 2 && secondImage ? (
            <ImageButton
              className={cn(styles.imageWrapper, styles.imageWrapperBig1)}
              onClick={() => {
                handleImageClick(1);
              }}
            >
              <AiBadge
                aiType={secondImage.aiType}
                t={tPropertyExpose as TFunction}
                translationKey={getAiBadgeTranslationKey({ size: 'm' })}
                offset="m"
                size="m"
                side="left"
              />

              <ImageWithFallBack
                className={styles.image}
                src={getUploadCareImageUrl({ imageId: secondImage.id, useWaterMark: listing?.hasWaterMark })}
                alt={getImageAltWithAiIndicator({
                  baseAlt: secondImage.altText || secondImage.title,
                  imageAiType: secondImage.aiType,
                  t: tPropertyExpose as TFunction,
                })}
                fill
                onLoad={(event) => onHeroImageLoad(event, 'secondImage')}
                style={
                  {
                    '--object-fit': portraitByHeroSlot.secondImage ? 'contain' : 'cover',
                    '--object-position': getHeroImageObjectPosition(portraitByHeroSlot.secondImage, secondImage),
                  } as CSSProperties
                }
                loading="lazy"
                sizes="(min-width: 904px) 564px, 0px"
                {...getUploadcareImageTransformationProps({ aspectRatio: '3/2', fit: 'automatic', image: secondImage })}
              />
            </ImageButton>
          ) : (
            slicedImages.slice(1, 3).map((image, index) => {
              const slot: HeroImagePortraitSlot = index === 0 ? 'secondImage' : 'thirdImage';
              const imageIndex = index + 1;

              return (
                <ImageButton
                  key={`property-hero-small-image-${index}`}
                  className={cn(styles.imageWrapper, index === 0 ? styles.imageWrapperSmall0 : styles.imageWrapperSmall1)}
                  onClick={() => {
                    handleImageClick(imageIndex);
                  }}
                  data-testid={`property-hero-small-image-${imageIndex}`}
                >
                  <AiBadge
                    aiType={image.aiType}
                    t={tPropertyExpose as TFunction}
                    translationKey={getAiBadgeTranslationKey({ size: 'm' })}
                    offset="m"
                    size="m"
                    side="left"
                  />

                  <ImageWithFallBack
                    className={styles.image}
                    src={getUploadCareImageUrl({ imageId: image.id, useWaterMark: listing?.hasWaterMark })}
                    alt={getImageAltWithAiIndicator({
                      baseAlt: image.altText || image.title,
                      imageAiType: image.aiType,
                      t: tPropertyExpose as TFunction,
                    })}
                    fill
                    onLoad={(event) => onHeroImageLoad(event, slot)}
                    style={
                      {
                        '--object-fit': portraitByHeroSlot[slot] ? 'contain' : 'cover',
                        '--object-position': getHeroImageObjectPosition(portraitByHeroSlot[slot], image),
                      } as CSSProperties
                    }
                    loading="lazy"
                    sizes="(min-width: 904px) 363px, 0px"
                    {...getUploadcareImageTransformationProps({ aspectRatio: '3/2', fit: 'automatic', image })}
                  />
                </ImageButton>
              );
            })
          )}

          {showsButtonGroup && (
            <ActionButtons
              focusedImageIndex={carouselActions.selectedIndex + 1}
              totalImages={genericListing.propertyImages?.length ?? 0}
              hasFloorPlan={Boolean(listing?.floorPlanImages?.length)}
              vTourType={vTourType}
              onBtnClick={(tab) => {
                setSelectedMediaTab(tab);
                openMediaModal();
              }}
            />
          )}
        </div>

        <MobileHeroImageSlider handleImageClick={handleImageClick} showsButtonGroup={showsButtonGroup} />
      </div>
    </Wrapper>
  );
};
